<script lang="ts">
  import Tooltip from "$lib/components/basic/tooltip/tooltip.svelte";
  import { Button } from "$lib/components/ui";
  import { authStore } from "$lib/stores";
  import { cn } from "$lib/utils";
  import { BadgeCheck, Box, Pencil, Trash } from "lucide-svelte";

  const {
    name,
    description,
    categories,
    className,
    isOfficial,
    prepopulated = false,
    showInCreateDialog = false,
    showActions = false,
    createdBy,
    id,
    onDelete,
    onEdit,
  }: {
    name: string;
    description: string;
    categories: { name: string; id: string }[];
    className?: string;
    isOfficial?: boolean;
    prepopulated?: boolean;
    showInCreateDialog?: boolean;
    showActions?: boolean;
    createdBy?: number;
    id: string;
    onDelete?: (id: string) => void;
    onEdit?: () => void;
  } = $props();

  const isAdmin = $derived(authStore.isAdmin());
  const isCreator = $derived($authStore.user?.id === createdBy);
  const isEditable = $derived(showActions && (isAdmin || isCreator));
</script>

<div
  class={cn(
    "relative flex w-full flex-col justify-between gap-2 rounded-lg border p-4",
    "border-zinc-400 dark:border-white/30",
    className,
  )}
>
  {#if isEditable}
    <div class="absolute right-4 top-4 flex items-center justify-center gap-2">
      <Button
        onclick={(e) => {
          e.stopPropagation();
          onEdit?.();
        }}
        variant="ghost"
        size="icon"
        class="size-6"
      >
        <Pencil class="text-theme-text-secondary size-3 stroke-1" />
      </Button>

      <Button
        onclick={(e) => {
          e.stopPropagation();
          onDelete?.(id);
        }}
        variant="ghost"
        size="icon"
        class="size-6"
      >
        <Trash class="size-3 stroke-1 text-theme-red" />
      </Button>
    </div>
  {/if}
  {#if isEditable}
    <div class="absolute bottom-4 right-4 flex items-center justify-center gap-2">
      <Tooltip content={showInCreateDialog ? "Show in create dialog" : "Not show in create dialog"}>
        <div class="rounded-base border border-theme-green-medium px-2 py-0.5 text-2xs text-theme-green-medium">
          {showInCreateDialog ? "S" : "NS"}
        </div>
      </Tooltip>
      <Tooltip content={prepopulated ? "Pre-populated" : "Not pre-populated"}>
        <div class="rounded-base border border-theme-green-medium px-2 py-0.5 text-2xs text-theme-green-medium">
          {prepopulated ? "P" : "NP"}
        </div>
      </Tooltip>
    </div>
  {/if}
  <div class="flex flex-col">
    <h3 class="flex items-center gap-2 text-base font-medium tracking-tight">
      <Box class="size-5 stroke-1 text-theme-text-secondary" />

      <span class=" max-w-48 truncate">
        {name}
      </span>
      {#if isOfficial}
        <Tooltip content="Chatsheet official template">
          <BadgeCheck class="size-5 text-theme-green-deep" />
        </Tooltip>
      {/if}
    </h3>
    <p class="text-2xs text-theme-text-secondary">{description}</p>
  </div>
  <div class="mt-5 flex flex-col">
    <div class="flex flex-wrap gap-2">
      {#each categories as category}
        <p class="rounded-base bg-theme-gray-100 px-2 py-1 text-2xs text-theme-text-secondary dark:bg-theme-gray-800">
          {category.name}
        </p>
      {/each}
    </div>
  </div>
</div>
